import { Routes, Route, Navigate } from 'react-router-dom';
import { useState, type ReactNode } from 'react';
import ProjectContext from './features/planning/context/ProjectContext';
import { NotificationProvider } from './context/NotificationProvider';
import type { Session } from '@supabase/supabase-js';
import { useAuth } from './features/auth/hooks/useAuth';
import { isInviteRedirect, isPasswordRecovery } from './lib/supabase';
import LoginPage from './features/auth/components/LoginPage';
import LandingPage from './pages/LandingPage';
import DashboardPage from './pages/DashboardPage';
import PlanningPage from './pages/PlanningPage';
import MachineFormPage from './pages/MachineFormPage';
import SettingsPage from './pages/SettingsPage';
import UsersPage from './pages/UsersPage';
import ProjectsPage from './pages/ProjectsPage';
import ProjectHistoryPage from './pages/ProjectHistoryPage';
import SitesPage from './pages/SitesPage';
import SubcontractorsPage from './pages/SubcontractorsPage';
import SuppliersPage from './pages/SuppliersPage';
import MachineDataPage from './pages/MachineDataPage';
import AccountPage from './pages/AccountPage';
import HelpPage from './pages/HelpPage';
import AdminPage from './pages/AdminPage';
import SetPasswordPage from './features/auth/components/SetPasswordPage';
import ResetPasswordPage from './features/auth/components/ResetPasswordPage';
import Spinner from './components/Spinner';

interface ProtectedRouteProps {
  session: Session | null;
  children: ReactNode;
}

function ProtectedRoute({ session, children }: ProtectedRouteProps) {
  if (!session) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

interface AdminRouteProps {
  session: Session | null;
  isSuperAdmin: boolean;
  children: ReactNode;
}

function AdminRoute({ session, isSuperAdmin, children }: AdminRouteProps) {
  if (!session) return <Navigate to="/login" replace />;
  if (!isSuperAdmin) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export default function App() {
  const { session, loading, orgId, isSuperAdmin, role, authError, clearAuthError } = useAuth();
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    () => localStorage.getItem('lastProjectId') ?? null,
  );

  const handleProjectChange = (id: string) => {
    setSelectedProjectId(id);
    localStorage.setItem('lastProjectId', id);
  };

  if (isInviteRedirect) return <SetPasswordPage />;
  if (isPasswordRecovery) return <ResetPasswordPage />;

  if (loading)
    return (
      <div className="h-dvh flex items-center justify-center bg-background">
        <div className="flex items-center gap-2 text-text-disabled text-sm">
          <Spinner size="sm" />
          Laden...
        </div>
      </div>
    );

  return (
    <NotificationProvider>
    <ProjectContext.Provider value={{ orgId, selectedProjectId, onProjectChange: handleProjectChange }}>
    <Routes>
      <Route
        path="/login"
        element={
          session ? (
            <Navigate to="/" replace />
          ) : (
            <LoginPage authError={authError} clearAuthError={clearAuthError} />
          )
        }
      />
      <Route
        path="/"
        element={
          session ? (
            <DashboardPage session={session} orgId={orgId} selectedProjectId={selectedProjectId} isSuperAdmin={isSuperAdmin} role={role} />
          ) : (
            <LandingPage />
          )
        }
      />
      <Route
        path="/planning"
        element={
          <ProtectedRoute session={session}>
            <PlanningPage orgId={orgId} selectedProjectId={selectedProjectId} isSuperAdmin={isSuperAdmin} role={role} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/machines/new"
        element={
          <ProtectedRoute session={session}>
            <MachineFormPage orgId={orgId} selectedProjectId={selectedProjectId} isSuperAdmin={isSuperAdmin} role={role} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute session={session}>
            <SettingsPage orgId={orgId} isSuperAdmin={isSuperAdmin} role={role} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute session={session}>
            <UsersPage session={session!} orgId={orgId} isSuperAdmin={isSuperAdmin} role={role} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects"
        element={
          <ProtectedRoute session={session}>
            <ProjectsPage orgId={orgId} isSuperAdmin={isSuperAdmin} role={role} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/project-history"
        element={
          <ProtectedRoute session={session}>
            <ProjectHistoryPage orgId={orgId} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sites"
        element={
          <ProtectedRoute session={session}>
            <SitesPage orgId={orgId} isSuperAdmin={isSuperAdmin} role={role} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subcontractors"
        element={
          <ProtectedRoute session={session}>
            <SubcontractorsPage orgId={orgId} isSuperAdmin={isSuperAdmin} role={role} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/suppliers"
        element={
          <ProtectedRoute session={session}>
            <SuppliersPage orgId={orgId} isSuperAdmin={isSuperAdmin} role={role} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/machine-data"
        element={
          <ProtectedRoute session={session}>
            <MachineDataPage orgId={orgId} isSuperAdmin={isSuperAdmin} role={role} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/account"
        element={
          <ProtectedRoute session={session}>
            <AccountPage session={session!} projectId={selectedProjectId} />
          </ProtectedRoute>
        }
      />
      <Route path="/help" element={<HelpPage isSuperAdmin={isSuperAdmin} />} />
      <Route
        path="/admin"
        element={
          <AdminRoute session={session} isSuperAdmin={isSuperAdmin}>
            <AdminPage orgId={orgId} />
          </AdminRoute>
        }
      />
      <Route path="/set-password" element={<SetPasswordPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </ProjectContext.Provider>
    </NotificationProvider>
  );
}
